import { observable, makeObservable } from 'mobx';
import orderBy from 'lodash/orderBy';

import { OfficeData, OfficeStatus } from 'chaika-frontend-types/frontend-cms';

import { List } from '@store/abstract/List';

import { Office } from './Office';
import { Validation } from '@utils/Validation';
import { removeNotNumbers } from '@utils/removeNotNumbers';

interface FilterParams {
    organizationId?: string;
    status?: OfficeStatus;
}

export class OfficeList extends List<Office, OfficeData, FilterParams> {
    public static Name = 'officeList' as const;

    @observable protected loadedOrganizationIds: Set<string> = new Set();

    constructor(params?: OfficeData[]) {
        super(params);
        makeObservable(this);
    }

    public isOrganizationDevicesLoaded(organizationId: string): boolean {
        return this.loadedOrganizationIds.has(organizationId);
    }

    public addLoadedOrganizationId(organizationId: string): void {
        this.loadedOrganizationIds.add(organizationId);
    }

    public getOrganizationActiveOfficeIds(organizationId: string): string[] {
        return this.getFilteredValuesIds({ organizationId, status: OfficeStatus.Active });
    }

    public getOrganizationOfficeIds(organizationId: string): string[] {
        return this.getFilteredValuesIds({ organizationId });
    }

    protected filterValue(value: OfficeData, { organizationId, status }: FilterParams): boolean {
        return (
            (!!organizationId ? value.organizationId === organizationId : true) &&
            (!!status ? value.status === status : true)
        );
    }

    protected create(params: OfficeData): Office {
        return new Office(params);
    }

    protected getOrderedValues(params: Office[]): Office[] {
        return [
            ...orderBy(
                params.filter(({ name }) => Validation.isNumeric(name) || /^\d/.test(name)),
                ({ name }) => Number(removeNotNumbers(name)),
            ),
            ...orderBy(
                params.filter(({ name }) => !Validation.isNumeric(name) && !/^\d/.test(name)),
                ({ name }) => name,
            ),
        ];
    }
}

export const officeList = new OfficeList();
